import Masthead, { MastheadLink } from "@amzn/meridian/esm/components/masthead";
import Theme from "@amzn/meridian/esm/components/theme";
import Box from "@amzn/meridian/esm/components/box";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import { SESSION_TABS, VIEWS, PILL_RADIUS_FILL } from "../../constants";
import { THEME_COLOR } from "../../styles/colors";

// Reduced tab-label font (Meridian's masthead default is 16) + equal per-tab width, so all
// tabs fit the narrow panel and every tab's selected underline is the same size.
const TAB_FONT_SIZE = 13;
const TAB_MIN_WIDTH = "96px";

/**
 * CountPill - solid teal, fully-rounded count badge shown next to a tab label (e.g. "2/4").
 */
const CountPill = ({ label }) => (
  <Box spacingInset="none none none 200">
    <Theme tokens={{ boxBorderRadiusFill: PILL_RADIUS_FILL }}>
      <Box type="fill" backgroundColor={THEME_COLOR.TEAL_GREEN} spacingInset="100 200">
        <Text type="b100" color="inverted">{label}</Text>
      </Box>
    </Theme>
  </Box>
);

/**
 * TabContent - a tab's label (+ optional pill) centered in an equal-min-width box, so every
 * tab is the same width and its underline comes out the same size.
 */
const TabContent = ({ label, pill }) => (
  <Box minWidth={TAB_MIN_WIDTH}>
    <Row width="100%" spacing="none" alignmentHorizontal="center" alignmentVertical="center">
      {label}
      {pill != null && <CountPill label={pill} />}
    </Row>
  </Box>
);

/**
 * SessionHeader - tab navigation for the active session: Assistant, Agenda (completed/total
 * count pill), and Action Items (count pill). Each pill follows its own collection, which is
 * already feature-flag gated upstream, so no flag is re-read here;
 * insightStore.counts drives both pills.
 */
const SessionHeader = ({ view, selectedTab, onTabClick, insightStore }) => {
  const { agenda, actionItems: actionItemsCount } = insightStore?.counts ?? {};
  const agendaPill = agenda?.total > 0 ? `${agenda.completed}/${agenda.total}` : null;
  const actionItemsPill = actionItemsCount > 0 ? String(actionItemsCount) : null;

  return (
    <Theme
      tokens={{
        mastheadLinkIndicatorColor: THEME_COLOR.TEAL_GREEN,
        mastheadTextFontSize: TAB_FONT_SIZE,
        mastheadLinkSpacingInsetHorizontal: 8,
        // Trim the leading gap so the space before the first tab ≈ the space after the last.
        mastheadSpacingHorizontal: 12,
      }}
    >
      <Masthead size="large">
        <MastheadLink
          selected={view === VIEWS.MAIN && selectedTab === SESSION_TABS.ASSISTANT}
          onClick={onTabClick(SESSION_TABS.ASSISTANT)}
        >
          <TabContent label="Assistant" />
        </MastheadLink>
        <MastheadLink
          selected={view === VIEWS.MAIN && selectedTab === SESSION_TABS.AGENDA}
          onClick={onTabClick(SESSION_TABS.AGENDA)}
        >
          <TabContent label="Agenda" pill={agendaPill} />
        </MastheadLink>
        <MastheadLink
          selected={view === VIEWS.MAIN && selectedTab === SESSION_TABS.ACTION_ITEMS}
          onClick={onTabClick(SESSION_TABS.ACTION_ITEMS)}
        >
          <TabContent label="Action Items" pill={actionItemsPill} />
        </MastheadLink>
      </Masthead>
    </Theme>
  );
};

export default SessionHeader;
